import { COMMISSION_TYPE_V3_DISPLAY_FALLBACK } from "../constants/applicationConstants";

export type ActivityTabKey = "all" | "order" | "invoice" | "payment" | "commission";

export type ActivityCenterQuery = {
  tab?: ActivityTabKey | string;
  orderId?: number;
  orderNumber?: string;
  invoiceId?: number;
  invoiceNumber?: string;
};

export type ActivityCenterAuditLog = {
  auditLogId?: number | string;
  entityId?: number | string;
  entityName?: string;
  entityNumber?: string;
  action?: string;
  actionType?: string;
  auditMessage?: string;
  auditTimestamp?: string;
  auditedOn?: string;
  createdTimestamp?: string;
  userName?: string;
  auditUser?: { userName?: string | null } | null;
};

export type HighlightSegment = {
  text: string;
  highlight?: boolean;
};

const VALID_TABS: ActivityTabKey[] = ["all", "order", "invoice", "payment", "commission"];

export const toPositiveId = (value: unknown): number | undefined => {
  const id = Number(value);
  if (!Number.isFinite(id) || id <= 0) return undefined;
  return id;
};

export const asEntityNumber = (value: unknown): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

export const parseActivityCenterSearch = (search: string): ActivityCenterQuery => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const tab = params.get("tab") || undefined;
  return {
    tab: tab && VALID_TABS.includes(tab as ActivityTabKey) ? (tab as ActivityTabKey) : undefined,
    orderId: toPositiveId(params.get("orderId")),
    orderNumber: asEntityNumber(params.get("orderNumber")),
    invoiceId: toPositiveId(params.get("invoiceId")),
    invoiceNumber: asEntityNumber(params.get("invoiceNumber")),
  };
};

export const buildActivityCenterPath = (query: ActivityCenterQuery): string => {
  const params = new URLSearchParams();
  const tab = query.tab && VALID_TABS.includes(query.tab as ActivityTabKey) ? query.tab : undefined;
  if (tab) params.set("tab", tab);
  if (query.orderId != null) {
    const orderId = toPositiveId(query.orderId);
    if (orderId) params.set("orderId", String(orderId));
  }
  const orderNumber = asEntityNumber(query.orderNumber);
  if (orderNumber) params.set("orderNumber", orderNumber);
  if (query.invoiceId != null) {
    const invoiceId = toPositiveId(query.invoiceId);
    if (invoiceId) params.set("invoiceId", String(invoiceId));
  }
  const invoiceNumber = asEntityNumber(query.invoiceNumber);
  if (invoiceNumber) params.set("invoiceNumber", invoiceNumber);
  const qs = params.toString();
  return qs ? `/activity_center?${qs}` : "/activity_center";
};

export type RelatedInvoiceRef = {
  invoiceId?: number;
  invoiceNumber?: string;
};

const addRelatedInvoice = (bucket: RelatedInvoiceRef[], id?: unknown, number?: unknown) => {
  const invoiceId = toPositiveId(id);
  const invoiceNumber = asEntityNumber(number);
  if (!invoiceId && !invoiceNumber) return;
  const match = bucket.find(
    (item) =>
      (invoiceId != null && item.invoiceId === invoiceId) ||
      (invoiceNumber != null && item.invoiceNumber === invoiceNumber)
  );
  if (match) {
    if (!match.invoiceId && invoiceId) match.invoiceId = invoiceId;
    if (!match.invoiceNumber && invoiceNumber) match.invoiceNumber = invoiceNumber;
    return;
  }
  bucket.push({ invoiceId, invoiceNumber });
};

const asRecordList = (value: any): any[] => {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
};

export const collectRelatedInvoices = (
  query: ActivityCenterQuery,
  order?: any,
  invoice?: any,
  extraItems?: any[]
): RelatedInvoiceRef[] => {
  const bucket: RelatedInvoiceRef[] = [];
  addRelatedInvoice(bucket, query.invoiceId, query.invoiceNumber);
  addRelatedInvoice(bucket, invoice?.invoiceId, invoice?.mfgInvoiceNumber || invoice?.invoiceNumber);
  addRelatedInvoice(bucket, order?.invoiceId, order?.mfgInvoiceNumber || order?.invoiceNumber);
  [...asRecordList(order?.orderItems), ...asRecordList(invoice?.orderItems), ...asRecordList(extraItems)].forEach(
    (item) => {
      addRelatedInvoice(bucket, item?.invoiceId, item?.mfgInvoiceNumber || item?.invoiceNumber);
      addRelatedInvoice(
        bucket,
        item?.invoice?.invoiceId,
        item?.invoice?.mfgInvoiceNumber || item?.invoice?.invoiceNumber
      );
    }
  );
  return bucket;
};

export const extractInvoiceListRows = (payload: any): any[] => {
  const data = payload?.data ?? payload;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.searchInvoiceData?.content)) return data.searchInvoiceData.content;
  if (Array.isArray(data)) return data;
  return [];
};

export const uniqueAuditLogRows = (rows: any[]): any[] => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = String(
      row?.auditLogId ??
        `${row?.entityName || ""}|${row?.entityNumber || ""}|${row?.auditTimestamp || row?.auditedOn || ""}|${row?.action || row?.auditMessage || ""}`
    );
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const humanizeActivityType = (value?: string | null): string => {
  if (!value) return "Activity";
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

export const logActionText = (log: ActivityCenterAuditLog): string => log.action || log.auditMessage || "";

export const isCommissionAuditMessage = (action?: string | null): boolean => {
  const text = action || "";
  if (!text) return false;
  return (
    /\bcommission\b/i.test(text) ||
    /\bservice fee\b/i.test(text) ||
    /\bservice rate\b/i.test(text) ||
    /\bservice agency\b/i.test(text)
  );
};

export const classifyAuditLogTab = (
  entityName?: string | null,
  action?: string | null,
  _actionType?: string | null
): Exclude<ActivityTabKey, "all"> => {
  const entity = (entityName || "").toUpperCase();
  const text = action || "";
  if (entity === "ORDER") return "order";
  if (isCommissionAuditMessage(text)) return "commission";
  if (
    /^Payment of \[/.test(text) ||
    /^Void Payment of \[/.test(text) ||
    /^New Payment Detail is /i.test(text) ||
    /^Payment Detail is /i.test(text) ||
    /^Credit Memo \[/.test(text)
  ) {
    return "payment";
  }
  return "invoice";
};

const tabMatchesLog = (tab: ActivityTabKey, log: ActivityCenterAuditLog): boolean => {
  if (tab === "all") return true;
  const related = classifyAuditLogTab(log.entityName, logActionText(log), log.actionType);
  if (related === tab) return true;
  return tab === "invoice" && related === "commission";
};

export const parseAuditTimestampMs = (value?: string | null): number => {
  if (value == null || value === "") return 0;
  const text = String(value).trim();
  if (!text) return 0;
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(?:\.(\d+))?/);
  if (match) {
    const fraction = (match[3] || "0").padEnd(3, "0").slice(0, 3);
    const parsed = new Date(`${match[1]}T${match[2]}.${fraction}`).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }
  const parsed = new Date(text).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const actionSortRank = (value?: string | null): number => {
  const type = String(value || "").toUpperCase();
  if (type === "CREATE" || type === "ADD") return 0;
  if (type === "MODIFY" || type === "UPDATE") return 1;
  if (type === "ACCESS" || type === "VIEW") return 2;
  return 3;
};

export const splitHighlightSegments = (text?: string | null): HighlightSegment[] => {
  if (!text) return [];
  const parts: HighlightSegment[] = [];
  const pattern = /(\[[^\]]+\]|\$\d+(?:\.\d{2})?)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null = pattern.exec(text);
  while (match) {
    if (match.index > lastIndex) parts.push({ text: text.slice(lastIndex, match.index) });
    parts.push({ text: match[0], highlight: true });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (lastIndex < text.length) parts.push({ text: text.slice(lastIndex) });
  return parts;
};

const auditTimeMs = (log: ActivityCenterAuditLog): number =>
  parseAuditTimestampMs(log.auditTimestamp || log.auditedOn || log.createdTimestamp);

const compareLogsChronological = (a: ActivityCenterAuditLog, b: ActivityCenterAuditLog): number => {
  const delta = auditTimeMs(a) - auditTimeMs(b);
  if (delta !== 0) return delta;
  const typeDelta = actionSortRank(a.actionType) - actionSortRank(b.actionType);
  if (typeDelta !== 0) return typeDelta;
  const idA = Number(a.auditLogId);
  const idB = Number(b.auditLogId);
  if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) return idA - idB;
  return 0;
};

const withinWindow = (a: ActivityCenterAuditLog, b: ActivityCenterAuditLog, windowMs: number): boolean =>
  Math.abs(auditTimeMs(a) - auditTimeMs(b)) <= windowMs;

const sameUser = (a: ActivityCenterAuditLog, b: ActivityCenterAuditLog): boolean =>
  (a.userName || "") === (b.userName || "");

const sameEntity = (a: ActivityCenterAuditLog, b: ActivityCenterAuditLog): boolean =>
  (a.entityName || "") === (b.entityName || "") && String(a.entityNumber || "") === String(b.entityNumber || "");

const RE = {
  orderCreate: /^New Order \[(.+?)\] created with (\d+) order items$/,
  orderItemCreate: /^New Order Item of Product SKU \[(.+?)\] created\.?$/,
  orderStatus: /^Order \[(.+?)\] status changed from (.+) to (.+)$/,
  orderSerial: /^Order Item of Product SKU \[(.+?)\] updated with Serial \[(.+?)\]$/,
  orderPrice: /^Order Item Price is updated for (.+?)(?: \(([^)]+)\))? \[Old Price : (.+), New Price: (.+)\]$/,
  invoiceGenerated: /^Invoice \[(.+?)\] generated for Order \[(.+?)\](?: - Amount \[([^\]]+)\])?(?:, Late Fee \[([^\]]+)\])?/,
  serviceFeeAdded:
    /^Service Fee added for Agency \[(.+?)\] of type \[(.+?)\](?:, Service Rate \[([^\]]+)\])?(?:, Service Fee Amount \[([^\]]+)\])?/,
  serviceFeePaid:
    /^Service Fee Paid for Agency \[(.+?)\] of type \[(.+?)\](?:, Service Fee Amount \[([^\]]+)\])?/,
  serviceRateUpdated:
    /^Service Rate updated for Agency \[(.+?)\] of type \[(.+?)\] from \[([^\]]+)\] to \[([^\]]+)\](?:, Service Fee Amount updated from \[([^\]]+)\] to \[([^\]]+)\])?/,
  paymentEvent:
    /^Payment of \[([^\]]+)\] (received|failed|canceled)(?: \[[^\]]+\])? for Invoice \[(.+?)\](?: via \[(.+?)\])?/,
  voidPayment: /^Void Payment of \[([^\]]+)\] for Invoice \[(.+?)\]/,
  invoiceStatus: /^Invoice \[(.+?)\] status \[(.+) -> (.+)\] changed$/,
  paymentDetailCreate: /^New Payment Detail is created\.?$/,
};

const isOrderCreate = (log: ActivityCenterAuditLog) => RE.orderCreate.test(logActionText(log));
const isOrderItemCreate = (log: ActivityCenterAuditLog) => RE.orderItemCreate.test(logActionText(log));
const isOrderStatus = (log: ActivityCenterAuditLog) => RE.orderStatus.test(logActionText(log));
const isOrderSerial = (log: ActivityCenterAuditLog) => RE.orderSerial.test(logActionText(log));
const isOrderPrice = (log: ActivityCenterAuditLog) => RE.orderPrice.test(logActionText(log));
const isInvoiceGenerated = (log: ActivityCenterAuditLog) => RE.invoiceGenerated.test(logActionText(log));
const isServiceFeeAdded = (log: ActivityCenterAuditLog) => RE.serviceFeeAdded.test(logActionText(log));
const isServiceFeePaid = (log: ActivityCenterAuditLog) => RE.serviceFeePaid.test(logActionText(log));
const isServiceRateUpdated = (log: ActivityCenterAuditLog) => RE.serviceRateUpdated.test(logActionText(log));
const isPaymentEvent = (log: ActivityCenterAuditLog) =>
  RE.paymentEvent.test(logActionText(log)) || RE.voidPayment.test(logActionText(log));
const isInvoiceStatus = (log: ActivityCenterAuditLog) => RE.invoiceStatus.test(logActionText(log));
const isPaymentDetailCreate = (log: ActivityCenterAuditLog) => RE.paymentDetailCreate.test(logActionText(log));
const isAccess = (log: ActivityCenterAuditLog) => {
  const type = (log.actionType || "").toUpperCase();
  return type === "ACCESS" || type === "VIEW";
};

type IndexedLog = { log: ActivityCenterAuditLog; index: number };

const sortIndexed = (rows: IndexedLog[]): IndexedLog[] =>
  [...rows].sort((a, b) => {
    const delta = compareLogsChronological(a.log, b.log);
    if (delta !== 0) return delta;
    return a.index - b.index;
  });

const collectNear = (
  rows: IndexedLog[],
  used: Set<number>,
  seed: IndexedLog,
  windowMs: number,
  matches: (log: ActivityCenterAuditLog) => boolean,
  canJoin: (seedLog: ActivityCenterAuditLog, other: ActivityCenterAuditLog) => boolean
): IndexedLog[] => {
  const cluster = [seed];
  used.add(seed.index);
  let grew = true;
  while (grew) {
    grew = false;
    rows.forEach((row) => {
      if (used.has(row.index) || isAccess(row.log) || !matches(row.log)) return;
      const near = cluster.some((member) => withinWindow(member.log, row.log, windowMs));
      if (!near || !canJoin(seed.log, row.log)) return;
      used.add(row.index);
      cluster.push(row);
      grew = true;
    });
  }
  return sortIndexed(cluster);
};

const countPreservingOrder = (values: string[]): { key: string; count: number }[] => {
  const counts = new Map<string, number>();
  const order: string[] = [];
  values.forEach((value) => {
    if (!counts.has(value)) order.push(value);
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return order.map((key) => ({ key, count: counts.get(key) || 0 }));
};

const chipsFromLogs = (logs: ActivityCenterAuditLog[]): { label: string; color?: string }[] | undefined => {
  const labels: string[] = [];
  logs.forEach((log) => {
    if (log.entityName && !labels.includes(log.entityName)) labels.push(log.entityName);
    const number = log.entityNumber != null ? String(log.entityNumber) : "";
    if (number && !labels.includes(number)) labels.push(number);
  });
  return labels.length > 0 ? labels.map((label) => ({ label })) : undefined;
};

const joinLogs = (a: ActivityCenterAuditLog, b: ActivityCenterAuditLog): boolean =>
  sameUser(a, b) && sameEntity(a, b);

export const extractAuditLogRows = (payload: any): ActivityCenterAuditLog[] => {
  const content = payload?.data?.searchAuditData?.content;
  return Array.isArray(content) ? content : [];
};

export type MappedActivityEntry = {
  id: string;
  type: string;
  title: string;
  description?: string;
  descriptionSegments?: HighlightSegment[];
  timestamp: string;
  user?: string;
  chips?: { label: string; color?: string }[];
  relatedTab: Exclude<ActivityTabKey, "all">;
  auditLogId?: number | string;
};

const logUserName = (log: ActivityCenterAuditLog): string | undefined =>
  log.userName || log.auditUser?.userName || undefined;

const toMappedEntry = (
  log: ActivityCenterAuditLog,
  id: string,
  overrides?: Partial<MappedActivityEntry>
): MappedActivityEntry => {
  const description = overrides?.description ?? logActionText(log) ?? undefined;
  const type = (overrides?.type || log.actionType || "OTHER").toUpperCase();
  const rawUser = overrides?.user !== undefined ? overrides.user : logUserName(log);
  return {
    id,
    type,
    title: overrides?.title || humanizeActivityType(type),
    description,
    descriptionSegments: splitHighlightSegments(description),
    timestamp: overrides?.timestamp || log.auditTimestamp || log.auditedOn || "",
    user: displayActivityUser(rawUser),
    chips: overrides?.chips !== undefined ? overrides.chips : chipsFromLogs([log]),
    relatedTab: overrides?.relatedTab || classifyAuditLogTab(log.entityName, description, log.actionType),
    auditLogId: overrides?.auditLogId !== undefined ? overrides.auditLogId : log.auditLogId,
  };
};

export const mapAuditLogsToEntries = (
  logs: ActivityCenterAuditLog[],
  prefix: string
): MappedActivityEntry[] => {
  return logs.map((log, index) =>
    toMappedEntry(log, `${prefix}-${log.auditLogId ?? log.entityId ?? log.auditTimestamp ?? "log"}-${index}`)
  );
};

const summarizeOrderCreate = (members: ActivityCenterAuditLog[]): string => {
  const parent = members.find(isOrderCreate);
  const parentMatch = parent ? RE.orderCreate.exec(logActionText(parent)) : null;
  const items = members.filter(isOrderItemCreate);
  const skus = countPreservingOrder(
    items.map((item) => RE.orderItemCreate.exec(logActionText(item))?.[1] || "")
  ).filter((row) => row.key);
  const orderNumber = parentMatch?.[1] || parent?.entityNumber || members[0]?.entityNumber || "";
  const count = items.length || Number(parentMatch?.[2]) || 0;
  const skuText = skus.map((row) => `[${row.key}] ×${row.count}`).join(", ");
  return `Order [${orderNumber}] created with ${count} items${skuText ? ` (${skuText})` : ""}`;
};

const summarizeFulfill = (members: ActivityCenterAuditLog[]): string => {
  const status = members.find(isOrderStatus);
  const statusMatch = status ? RE.orderStatus.exec(logActionText(status)) : null;
  const serials = members.filter(isOrderSerial);
  const serialText = serials
    .map((row) => {
      const match = RE.orderSerial.exec(logActionText(row));
      return match ? `[${match[1]}] [${match[2]}]` : "";
    })
    .filter(Boolean)
    .join(", ");
  const orderNumber = statusMatch?.[1] || status?.entityNumber || members[0]?.entityNumber || "";
  const fromStatus = statusMatch?.[2] || "";
  const toStatus = statusMatch?.[3] || "";
  return `Order [${orderNumber}] [${fromStatus}] → [${toStatus}]${serialText ? `; serials set for ${serialText}` : ""}`;
};

const summarizePrice = (members: ActivityCenterAuditLog[]): string => {
  const match = RE.orderPrice.exec(logActionText(members[0]));
  const size = match?.[1] || "";
  const serial = match?.[2];
  const oldPrice = match?.[3] || "";
  const newPrice = match?.[4] || "";
  return `Order item price ${size}${serial ? ` (${serial})` : ""}: ${oldPrice} → ${newPrice}`;
};

const summarizeInvoiceGenerated = (members: ActivityCenterAuditLog[]): string => {
  const generated = members.find(isInvoiceGenerated);
  const match = generated ? RE.invoiceGenerated.exec(logActionText(generated)) : null;
  const invoiceNumber = match?.[1] || generated?.entityNumber || "";
  const orderNumber = match?.[2] || "";
  const amount = match?.[3];
  const lateFee = match?.[4];
  const fees = members.filter(isServiceFeeAdded).map((row) => {
    const fee = RE.serviceFeeAdded.exec(logActionText(row));
    const agency = fee?.[1] || "Unknown";
    const type = fee?.[2] || "Unknown";
    const feeAmount = fee?.[4];
    return `${agency} (${type})${feeAmount ? ` ${feeAmount}` : ""}`;
  });
  const parts = [`Invoice [${invoiceNumber}] generated for Order [${orderNumber}]`];
  if (amount) parts.push(`Amount [${amount}]`);
  if (lateFee) parts.push(`Late Fee [${lateFee}]`);
  let summary = parts[0] + (parts.length > 1 ? ` · ${parts.slice(1).join(" · ")}` : "");
  if (fees.length) summary += `; Service Fee added: ${fees.join(", ")}`;
  return summary;
};

const summarizeServiceFees = (members: ActivityCenterAuditLog[], verb: "added" | "paid"): string => {
  const fees = members.map((row) => {
    const fee =
      verb === "paid"
        ? RE.serviceFeePaid.exec(logActionText(row))
        : RE.serviceFeeAdded.exec(logActionText(row));
    const agency = fee?.[1] || "Unknown";
    const type = fee?.[2] || "Unknown";
    const feeAmount = verb === "paid" ? fee?.[3] : fee?.[4];
    return `${agency} (${type})${feeAmount ? ` ${feeAmount}` : ""}`;
  });
  const invoiceNumber =
    members.find((row) => row.entityNumber)?.entityNumber ||
    /Invoice \[(.+?)\]/.exec(logActionText(members[0] || {}))?.[1] ||
    "";
  const prefix = verb === "paid" ? "Service Fee paid" : "Service Fee added";
  return `${prefix}: ${fees.join(", ")}${invoiceNumber ? ` from Invoice [${invoiceNumber}]` : ""}`;
};

const summarizeRateUpdates = (members: ActivityCenterAuditLog[]): string => {
  const updates = members.map((row) => {
    const match = RE.serviceRateUpdated.exec(logActionText(row));
    const agency = match?.[1] || "Unknown";
    const type = match?.[2] || "Unknown";
    const fromRate = match?.[3] || "";
    const toRate = match?.[4] || "";
    return `${agency} (${type}) [${fromRate}] → [${toRate}]`;
  });
  return `Service Rate updated: ${updates.join(", ")}`;
};

const summarizePayment = (members: ActivityCenterAuditLog[]): string => {
  const payment = members.find(isPaymentEvent) || members[0];
  const voidMatch = RE.voidPayment.exec(logActionText(payment));
  const payMatch = RE.paymentEvent.exec(logActionText(payment));
  const status = members.find(isInvoiceStatus);
  const statusMatch = status ? RE.invoiceStatus.exec(logActionText(status)) : null;
  let summary = logActionText(payment);
  if (voidMatch) {
    summary = `Payment of [${voidMatch[1]}] voided for Invoice [${voidMatch[2]}]`;
  } else if (payMatch) {
    const via = payMatch[4] ? ` via [${payMatch[4]}]` : "";
    summary = `Payment of [${payMatch[1]}] ${payMatch[2]} for Invoice [${payMatch[3]}]${via}`;
  }
  if (statusMatch) {
    summary += ` · status [${statusMatch[2]}] → [${statusMatch[3]}]`;
  }
  return summary;
};

const groupedEntry = (
  members: ActivityCenterAuditLog[],
  summary: string,
  relatedTab: Exclude<ActivityTabKey, "all">,
  id: string
): MappedActivityEntry => {
  const types = members.map((log) => (log.actionType || "OTHER").toUpperCase());
  const type = types.includes("CREATE") ? "CREATE" : types[0] || "OTHER";
  const newest = members.reduce((best, log) => (auditTimeMs(log) >= auditTimeMs(best) ? log : best), members[0]);
  return toMappedEntry(members[0], id, {
    type,
    title: humanizeActivityType(type),
    description: summary,
    timestamp: newest?.auditTimestamp || newest?.auditedOn || "",
    user: logUserName(members[0]),
    chips: chipsFromLogs(members),
    relatedTab,
    auditLogId: newest?.auditLogId ?? members[0]?.auditLogId,
  });
};

export const buildActivityTabEntries = (
  logs: ActivityCenterAuditLog[],
  tab: ActivityTabKey,
  prefix = tab
): MappedActivityEntry[] => {
  const indexed = sortIndexed(logs.map((log, index) => ({ log, index })));
  const used = new Set<number>();
  const grouped: MappedActivityEntry[] = [];

  const take = (
    seed: IndexedLog,
    windowMs: number,
    matches: (log: ActivityCenterAuditLog) => boolean,
    canJoin: (seedLog: ActivityCenterAuditLog, other: ActivityCenterAuditLog) => boolean
  ) => collectNear(indexed, used, seed, windowMs, matches, canJoin).map((row) => row.log);

  if (tab === "order" || tab === "all") {
    indexed.forEach((row) => {
      if (used.has(row.index) || !isOrderCreate(row.log)) return;
      const members = take(row, 1000, (log) => isOrderCreate(log) || isOrderItemCreate(log), joinLogs);
      if (members.length > 1) {
        grouped.push(groupedEntry(members, summarizeOrderCreate(members), "order", `${prefix}-g1-${row.index}`));
      } else {
        used.delete(row.index);
      }
    });
    indexed.forEach((row) => {
      if (used.has(row.index) || !isOrderStatus(row.log)) return;
      const members = take(row, 3000, (log) => isOrderStatus(log) || isOrderSerial(log), joinLogs);
      if (members.length > 1) {
        grouped.push(groupedEntry(members, summarizeFulfill(members), "order", `${prefix}-g2-${row.index}`));
      } else {
        used.delete(row.index);
      }
    });
  }

  if (tab === "order") {
    indexed.forEach((row) => {
      if (used.has(row.index) || !isOrderPrice(row.log)) return;
      const seedMatch = RE.orderPrice.exec(logActionText(row.log));
      const members = take(
        row,
        1000,
        isOrderPrice,
        (seedLog, other) => {
          if (!joinLogs(seedLog, other)) return false;
          const otherMatch = RE.orderPrice.exec(logActionText(other));
          return (
            (seedMatch?.[1] || "") === (otherMatch?.[1] || "") &&
            (seedMatch?.[3] || "") === (otherMatch?.[3] || "") &&
            (seedMatch?.[4] || "") === (otherMatch?.[4] || "")
          );
        }
      );
      if (members.length > 1) {
        grouped.push(groupedEntry(members, summarizePrice(members), "order", `${prefix}-g3-${row.index}`));
      } else {
        used.delete(row.index);
      }
    });
  }

  if (tab === "invoice" || tab === "all") {
    indexed.forEach((row) => {
      if (used.has(row.index) || (row.log.entityName || "").toUpperCase() !== "INVOICE" || !isInvoiceGenerated(row.log)) {
        return;
      }
      const generated = RE.invoiceGenerated.exec(logActionText(row.log));
      const members = take(
        row,
        3000,
        (log) => {
          if (isServiceFeeAdded(log) && sameUser(row.log, log) && sameEntity(row.log, log)) return true;
          if (tab !== "all" || (log.entityName || "").toUpperCase() !== "ORDER" || !isInvoiceGenerated(log)) return false;
          const other = RE.invoiceGenerated.exec(logActionText(log));
          return (
            (generated?.[1] || "") === (other?.[1] || "") &&
            (generated?.[3] || "") === (other?.[3] || "")
          );
        },
        () => true
      );
      if (members.length > 1) {
        grouped.push(
          groupedEntry(members, summarizeInvoiceGenerated(members), "invoice", `${prefix}-g4-${row.index}`)
        );
      } else {
        used.delete(row.index);
      }
    });
  }

  if (tab === "payment" || tab === "invoice" || tab === "all") {
    indexed.forEach((row) => {
      if (used.has(row.index) || !isPaymentEvent(row.log)) return;
      const members = take(
        row,
        3000,
        (log) => isPaymentEvent(log) || isInvoiceStatus(log) || isPaymentDetailCreate(log),
        (seedLog, other) => {
          if (!sameUser(seedLog, other) || !sameEntity(seedLog, other)) return false;
          if (isPaymentEvent(other) && other !== seedLog) return false;
          return true;
        }
      );
      const hasCompanion = members.some((log) => isInvoiceStatus(log) || isPaymentDetailCreate(log));
      if (hasCompanion) {
        grouped.push(groupedEntry(members, summarizePayment(members), "payment", `${prefix}-g6-${row.index}`));
      } else {
        used.delete(row.index);
      }
    });
  }

  if (tab === "commission" || tab === "all") {
    indexed.forEach((row) => {
      if (used.has(row.index) || !isServiceFeePaid(row.log)) return;
      const members = take(row, 3000, isServiceFeePaid, joinLogs);
      if (members.length > 1) {
        grouped.push(
          groupedEntry(members, summarizeServiceFees(members, "paid"), "commission", `${prefix}-g7-${row.index}`)
        );
      } else {
        used.delete(row.index);
      }
    });
  }

  if (tab === "commission" || tab === "invoice" || tab === "all") {
    indexed.forEach((row) => {
      if (used.has(row.index) || !isServiceFeeAdded(row.log)) return;
      const members = take(row, 3000, isServiceFeeAdded, joinLogs);
      if (members.length > 1) {
        grouped.push(
          groupedEntry(members, summarizeServiceFees(members, "added"), "commission", `${prefix}-g8-${row.index}`)
        );
      } else {
        used.delete(row.index);
      }
    });
    indexed.forEach((row) => {
      if (used.has(row.index) || !isServiceRateUpdated(row.log)) return;
      const members = take(row, 3000, isServiceRateUpdated, joinLogs);
      if (members.length > 1) {
        grouped.push(
          groupedEntry(members, summarizeRateUpdates(members), "commission", `${prefix}-g17-${row.index}`)
        );
      } else {
        used.delete(row.index);
      }
    });
  }

  const leftovers = indexed
    .filter((row) => !used.has(row.index))
    .filter((row) => tabMatchesLog(tab, row.log))
    .map((row) =>
      toMappedEntry(row.log, `${prefix}-${row.log.auditLogId ?? row.log.entityId ?? "log"}-${row.index}`)
    );

  return [...grouped, ...leftovers].sort((a, b) => {
    const timeDelta = parseAuditTimestampMs(b.timestamp) - parseAuditTimestampMs(a.timestamp);
    if (timeDelta !== 0) return timeDelta;
    const typeDelta = actionSortRank(b.type) - actionSortRank(a.type);
    if (typeDelta !== 0) return typeDelta;
    const idA = Number(a.auditLogId);
    const idB = Number(b.auditLogId);
    if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) return idB - idA;
    return 0;
  });
};

export const toAmountNumber = (value: unknown): number => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const sanitized = String(value).replace(/[^0-9.-]+/g, "");
  const parsed = parseFloat(sanitized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const displayEntityValue = (value?: string | null): string => {
  const trimmed = (value || "").trim();
  return trimmed || "—";
};

export const SYSTEM_GENERATED_USER = "System Generated";

export const displayActivityUser = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed || trimmed === "-" || trimmed === "—" || /^null|undefined$/i.test(trimmed)) {
    return SYSTEM_GENERATED_USER;
  }
  return trimmed;
};

export type CommissionBreakdownItem = {
  key: "v3" | "agency" | "override";
  label: string;
  amount: number;
  paid: boolean;
};

export type CommissionBreakdown = {
  total: number;
  items: CommissionBreakdownItem[];
};

const roundCurrency = (value: number) => Math.round(value * 100) / 100;

const firstAmount = (...values: unknown[]): number => {
  for (const value of values) {
    const amount = toAmountNumber(value);
    if (amount !== 0) return amount;
  }
  return 0;
};

const asGroupList = (group: any): any[] => {
  if (group == null) return [];
  return Array.isArray(group) ? group : [group];
};

const asPaymentList = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.payments)) return value.payments;
  return [];
};

const classifyCommissionType = (value: unknown): "v3" | "agency" | "override" | null => {
  const text = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
  if (!text) return null;
  if (text === "V3" || text === "PRIMARY ORG" || text === "PRIMARYORG") return "v3";
  if (text === "AGENCY") return "agency";
  if (text === "OVERRIDE") return "override";
  return null;
};

const isVoidPayment = (payment: any): boolean =>
  String(payment?.paymentStatus || "").toUpperCase() === "VOID";

/**
 * Invoice-detail nested groups store paid in `totalAmount` and unpaid in
 * `remainingAmount`. The header must show the full receivable/payable, so add
 * those together whenever remaining is present.
 */
const expectedFromNestedGroup = (group: any): number =>
  roundCurrency(
    asGroupList(group).reduce((sum, item) => {
      if (!item || typeof item !== "object") return sum;
      const explicit = firstAmount(item.expectedCommissionAmount, item.expectedAmount);
      if (explicit !== 0) return sum + explicit;
      const hasRemaining = item.remainingAmount != null && item.remainingAmount !== "";
      const recorded = toAmountNumber(item.totalAmount);
      if (hasRemaining) return sum + recorded + toAmountNumber(item.remainingAmount);
      return sum + recorded;
    }, 0)
  );

const paidFromNestedGroup = (group: any): number =>
  asGroupList(group).reduce((sum, item) => {
    if (!item || typeof item !== "object") return sum;
    const explicitPaid = firstAmount(item.paidAmount, item.receivedAmount);
    if (explicitPaid !== 0) return sum + explicitPaid;
    const hasRemaining = item.remainingAmount != null && item.remainingAmount !== "";
    if (hasRemaining) return sum + toAmountNumber(item.totalAmount);
    const infos = Array.isArray(item.commissionInfo) ? item.commissionInfo : [];
    return (
      sum +
      infos.reduce((infoSum: number, info: any) => infoSum + toAmountNumber(info?.commissionAmount), 0)
    );
  }, 0);

const hasCommissionType = (amount: number, group: any): boolean => {
  if (amount !== 0) return true;
  const groups = asGroupList(group);
  return groups.some((item) => {
    if (!item || typeof item !== "object") return false;
    if (item.commissionRate != null) return true;
    if (item.expectedCommissionRate != null) return true;
    if (Array.isArray(item.commissionInfo) && item.commissionInfo.length > 0) return true;
    if (toAmountNumber(item.totalAmount) !== 0) return true;
    if (toAmountNumber(item.remainingAmount) !== 0) return true;
    return false;
  });
};

const remainingOf = (group: any): number | undefined => {
  const groups = asGroupList(group);
  let seen = false;
  let sum = 0;
  groups.forEach((item) => {
    if (item && item.remainingAmount != null && item.remainingAmount !== "") {
      seen = true;
      sum += toAmountNumber(item.remainingAmount);
    }
  });
  return seen ? sum : undefined;
};

const isCommissionFullyPaid = (
  expected: number,
  paidAmount: number,
  remaining: number | undefined,
  status?: unknown
): boolean => {
  const statusText = String(status || "").toUpperCase();
  if (statusText === "PAID" || statusText === "RECEIVED") return true;
  if (expected > 0 && paidAmount + 0.009 >= expected) return true;
  if (remaining != null && remaining <= 0.009 && expected > 0) return true;
  return false;
};

const remainingCommissionableAmount = (invoice: any, payments: any[]): number => {
  const invoiceTotal = toAmountNumber(invoice?.invoiceAmount);
  const discount = toAmountNumber(invoice?.discountAmount);
  const credit = toAmountNumber(invoice?.creditAmount);
  const commissionableBase = roundCurrency(Math.max(0, invoiceTotal - discount - credit));
  const paid = roundCurrency(
    payments.reduce((sum, payment) => {
      const amount =
        payment?.adjustedAmount != null
          ? toAmountNumber(payment.adjustedAmount)
          : toAmountNumber(payment?.paymentAmount ?? payment?.amount);
      return sum + amount;
    }, 0)
  );
  if (payments.length > 0 || invoiceTotal > 0) {
    return roundCurrency(Math.max(0, commissionableBase - paid));
  }
  return toAmountNumber(invoice?.payment?.remainingAmount ?? invoice?.outstandingAmt);
};

type TypeTotals = { amount: number; paid: number; present: boolean };

const emptyTypeTotals = (): Record<"v3" | "agency" | "override", TypeTotals> => ({
  v3: { amount: 0, paid: 0, present: false },
  agency: { amount: 0, paid: 0, present: false },
  override: { amount: 0, paid: 0, present: false },
});

const totalsFromInvoiceAgencies = (
  invoice: any,
  invoiceAgencies: any[],
  paymentsInput: any
): Record<"v3" | "agency" | "override", TypeTotals> | null => {
  if (!invoiceAgencies.length) return null;

  const payments = asPaymentList(paymentsInput).filter((payment) => !isVoidPayment(payment));
  const remainingInvoice = remainingCommissionableAmount(invoice, payments);
  const acc = emptyTypeTotals();

  invoiceAgencies.forEach((agency) => {
    const key = classifyCommissionType(agency?.agencyType);
    if (!key) return;
    acc[key].present = true;
    const rate = toAmountNumber(agency?.expectedCommissionRate);
    const matching = payments.flatMap((payment) =>
      asGroupList(payment?.commissions).filter((commission) => {
        if (classifyCommissionType(commission?.agencyType) !== key) return false;
        if (agency?.agencyId == null || commission?.agencyId == null) return true;
        return Number(commission.agencyId) === Number(agency.agencyId);
      })
    );
    const paymentExpected = roundCurrency(
      matching.reduce((sum, commission) => sum + toAmountNumber(commission?.expectedCommissionAmount), 0)
    );
    const paymentPaid = roundCurrency(
      matching.reduce((sum, commission) => sum + toAmountNumber(commission?.calculatedCommissionAmount), 0)
    );
    const unpaidCommission =
      remainingInvoice > 0 ? roundCurrency((remainingInvoice * rate) / 100) : 0;
    let expected =
      payments.length > 0
        ? roundCurrency(paymentExpected + unpaidCommission)
        : toAmountNumber(agency?.expectedCommissionAmount);
    if (expected === 0) expected = toAmountNumber(agency?.expectedCommissionAmount);
    acc[key].amount = roundCurrency(acc[key].amount + expected);
    acc[key].paid = roundCurrency(acc[key].paid + paymentPaid);
  });

  return acc;
};

export type CommissionBreakdownSource = {
  invoiceAgencies?: any[] | null;
  payments?: any[] | { payments?: any[] } | null;
  /** CommissionType displayValue for key V3 (from codetable). */
  v3EntityLabel?: string;
};

export const getCommissionBreakdown = (
  invoice: any,
  source?: CommissionBreakdownSource
): CommissionBreakdown => {
  const agencies = asGroupList(source?.invoiceAgencies).filter(Boolean);
  const fromAgencies = totalsFromInvoiceAgencies(invoice, agencies, source?.payments);

  const v3Amount =
    fromAgencies?.v3.present
      ? fromAgencies.v3.amount
      : firstAmount(
          invoice?.expectedV3Amt,
          expectedFromNestedGroup(invoice?.v3Commissions),
          invoice?.v3CommissionAmounts,
          invoice?.v3CommissionAmount
        );
  const agencyAmount =
    fromAgencies?.agency.present
      ? fromAgencies.agency.amount
      : firstAmount(
          invoice?.expectedAgencyAmt,
          expectedFromNestedGroup(invoice?.agencyCommissions),
          invoice?.agencyCommissionAmounts,
          invoice?.agencyCommissionAmount
        );
  const overrideAmount =
    fromAgencies?.override.present
      ? fromAgencies.override.amount
      : firstAmount(
          invoice?.expectedOvrAmt,
          expectedFromNestedGroup(invoice?.ovrAgencyCommissions),
          invoice?.ovrAgencyCommissionAmounts,
          invoice?.ovrAgencyCommissionAmount
        );

  const v3PaidAmount = fromAgencies?.v3.present
    ? fromAgencies.v3.paid
    : firstAmount(
        invoice?.v3CommissionPaidAmount,
        invoice?.totalV3Paid,
        paidFromNestedGroup(invoice?.v3Commissions)
      );
  const agencyPaidAmount = fromAgencies?.agency.present
    ? fromAgencies.agency.paid
    : firstAmount(
        invoice?.agencyCommissionPaidAmount,
        invoice?.totalAgencyPaid,
        paidFromNestedGroup(invoice?.agencyCommissions)
      );
  const overridePaidAmount = fromAgencies?.override.present
    ? fromAgencies.override.paid
    : firstAmount(
        invoice?.ovrAgencyCommissionPaidAmount,
        invoice?.totalOvrPaid,
        paidFromNestedGroup(invoice?.ovrAgencyCommissions)
      );

  const items: CommissionBreakdownItem[] = [];
  const v3EntityLabel = source?.v3EntityLabel?.trim() || COMMISSION_TYPE_V3_DISPLAY_FALLBACK;

  if (hasCommissionType(v3Amount, invoice?.v3Commissions) || (fromAgencies?.v3.present && v3Amount !== 0)) {
    items.push({
      key: "v3",
      label: v3EntityLabel,
      amount: v3Amount,
      paid: isCommissionFullyPaid(v3Amount, v3PaidAmount, remainingOf(invoice?.v3Commissions), invoice?.v3CommStatus),
    });
  }
  if (hasCommissionType(agencyAmount, invoice?.agencyCommissions) || (fromAgencies?.agency.present && agencyAmount !== 0)) {
    items.push({
      key: "agency",
      label: "Agency",
      amount: agencyAmount,
      paid: isCommissionFullyPaid(
        agencyAmount,
        agencyPaidAmount,
        remainingOf(invoice?.agencyCommissions),
        invoice?.agencyCommStatus
      ),
    });
  }
  if (
    hasCommissionType(overrideAmount, invoice?.ovrAgencyCommissions) ||
    (fromAgencies?.override.present && overrideAmount !== 0)
  ) {
    items.push({
      key: "override",
      label: "Override",
      amount: overrideAmount,
      paid: isCommissionFullyPaid(
        overrideAmount,
        overridePaidAmount,
        remainingOf(invoice?.ovrAgencyCommissions),
        invoice?.ovrCommStatus
      ),
    });
  }

  return {
    total: roundCurrency(items.reduce((sum, item) => sum + item.amount, 0)),
    items,
  };
};
